import { apiDelete, apiGet, apiPatch } from '../../services/apiClient';
import { getLocalItem, getSessionItem, setLocalItem, setSessionItem } from '../../utils/safeStorage';

const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
const ADMIN_ACCESS_KEY = 'adminAccessToken';
const LEGACY_ADMIN_ACCESS_KEY = 'hv_admin_token';

const parseMessage = async (res) => {
  const text = await res.text();
  if (!text) return res.ok ? '' : 'Request failed';
  try {
    const parsed = JSON.parse(text);
    return parsed?.message || text;
  } catch (_) {
    return text;
  }
};

const refreshAdminToken = async () => {
  try {
    const res = await fetch(`${API_BASE}/api/admin/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
    });
    if (!res.ok) return null;
    const data = await res.json().catch(() => ({}));
    const nextToken = data?.accessToken || data?.token || '';
    if (!nextToken) return null;
    setSessionItem(ADMIN_ACCESS_KEY, nextToken);
    setLocalItem(ADMIN_ACCESS_KEY, nextToken);
    setSessionItem(LEGACY_ADMIN_ACCESS_KEY, nextToken);
    setLocalItem(LEGACY_ADMIN_ACCESS_KEY, nextToken);
    return nextToken;
  } catch {
    return null;
  }
};

const LEGACY_PATH = '/api/admin/hero-banners-legacy';
const MODERN_PATH = '/api/admin/hero-banners';

const shouldFallback = (err) =>
  [403, 404, 405].includes(err?.status);

const withFallback = async (legacyCall, modernCall) => {
  try {
    return await legacyCall();
  } catch (err) {
    if (!shouldFallback(err)) throw err;
    return modernCall();
  }
};

const resolveAdminToken = async (token) => {
  if (token) return token;
  const stored =
    getSessionItem(ADMIN_ACCESS_KEY) ||
    getLocalItem(ADMIN_ACCESS_KEY) ||
    getSessionItem(LEGACY_ADMIN_ACCESS_KEY) ||
    getLocalItem(LEGACY_ADMIN_ACCESS_KEY) ||
    null;
  if (stored) return stored;
  return refreshAdminToken();
};

const requestMultipart = async (method, path, formData, token) => {
  const effectiveToken = await resolveAdminToken(token);
  const doFetch = (authToken) =>
    fetch(`${API_BASE}${path}`, {
      method,
      headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
      credentials: 'include',
      body: formData,
    });

  let res = await doFetch(effectiveToken);
  if (res.status === 401) {
    const refreshed = await refreshAdminToken();
    if (refreshed) res = await doFetch(refreshed);
  }

  if (!res.ok) {
    const message = await parseMessage(res);
    const err = new Error(message || 'Request failed');
    err.status = res.status;
    throw err;
  }

  const text = await res.text();
  return text ? JSON.parse(text) : null;
};

export const listHeroBanners = async (token) =>
  withFallback(
    () => apiGet(LEGACY_PATH, token),
    () => apiGet(MODERN_PATH, token)
  );

export const createHeroBanner = async (payload, file, token) => {
  const form = new FormData();
  Object.entries(payload || {}).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    form.append(key, String(value));
  });
  if (file) form.append('image', file);
  return withFallback(
    () => requestMultipart('POST', LEGACY_PATH, form, token),
    () => requestMultipart('POST', MODERN_PATH, form, token)
  );
};

export const updateHeroBanner = async (id, payload, file, token) => {
  const form = new FormData();
  Object.entries(payload || {}).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    form.append(key, String(value));
  });
  if (file) form.append('image', file);
  return withFallback(
    () => requestMultipart('PUT', `${LEGACY_PATH}/${id}`, form, token),
    () => requestMultipart('PUT', `${MODERN_PATH}/${id}`, form, token)
  );
};

export const toggleHeroBanner = async (id, status, token) =>
  withFallback(
    () => apiPatch(`${LEGACY_PATH}/${id}/toggle`, { status }, token),
    () => apiPatch(`${MODERN_PATH}/${id}/toggle`, { status }, token)
  );

export const reorderHeroBanners = async (items, token) =>
  withFallback(
    () => apiPatch(`${LEGACY_PATH}/reorder`, { items }, token),
    () => apiPatch(`${MODERN_PATH}/reorder`, { items }, token)
  );

export const deleteHeroBanner = async (id, token) =>
  withFallback(
    () => apiDelete(`${LEGACY_PATH}/${id}`, token),
    () => apiDelete(`${MODERN_PATH}/${id}`, token)
  );


